import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { LanguageSelector } from '../components/LanguageSelector';
import BrandMark from '../components/BrandMark';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Lock, CheckCircle, XCircle, Eye, EyeOff, Sun, Moon } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsNotMatch'));
      return;
    }
    
    if (password.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t('auth.passwordReset'));
    } catch (err) {
      setError(true);
      toast.error(err.response?.data?.detail || t('auth.expiredLinkMessage'));
    } finally {
      setLoading(false);
    }
  };

  // Card styles based on theme
  const cardStyles = isDark 
    ? 'bg-[#0F0F2F] border-purple-500/30' 
    : 'bg-white border-slate-200';
  
  const bgStyles = isDark 
    ? 'bg-[#0B0F2A]' 
    : 'bg-gradient-to-br from-slate-50 to-slate-100';

  if (!token) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${bgStyles}`}>
        <Card className={`max-w-md w-full ${cardStyles}`}>
          <CardContent className="p-6 sm:p-8 text-center">
            <XCircle className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('auth.invalidLink')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.invalidLinkMessage')}
            </p>
            <Link to="/forgot-password">
              <Button className={`w-full ${
                isDark 
                  ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                  : 'bg-primary hover:bg-primary/90'
              }`}>
                {t('auth.requestNewLink')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${bgStyles}`}>
        <Card className={`max-w-md w-full ${cardStyles}`}>
          <CardContent className="p-6 sm:p-8 text-center">
            <CheckCircle className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('auth.passwordReset')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.passwordResetSuccess')}
            </p>
            <Link to="/login">
              <Button className={`w-full ${
                isDark 
                  ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                  : 'bg-primary hover:bg-primary/90'
              }`}>
                {t('auth.goToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${bgStyles}`}>
        <Card className={`max-w-md w-full ${cardStyles}`}>
          <CardContent className="p-6 sm:p-8 text-center">
            <XCircle className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('auth.expiredLink')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.expiredLinkMessage')}
            </p>
            <Link to="/forgot-password">
              <Button className={`w-full ${
                isDark 
                  ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                  : 'bg-primary hover:bg-primary/90'
              }`}>
                {t('auth.requestNewLink')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${bgStyles}`}>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <BrandMark variant="wordmark" theme={isDark ? 'dark' : 'light'} size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleTheme}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                isDark 
                  ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSelector />
          </div>
        </div>

        <Card className={`${cardStyles} shadow-xl`}>
          <CardContent className="p-6 sm:p-8">
            <div className={`w-12 h-12 sm:w-14 sm:h-14 mb-4 sm:mb-6 rounded-xl flex items-center justify-center ${
              isDark ? 'bg-purple-500/20' : 'bg-primary/10'
            }`}>
              <Lock className={`w-6 h-6 sm:w-7 sm:h-7 ${isDark ? 'text-[#9D4DFF]' : 'text-primary'}`} />
            </div>
            
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('auth.resetPasswordTitle')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.resetPasswordSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className={isDark ? 'text-gray-300' : ''}>{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-password"
                    className={`pr-10 h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{t('auth.passwordMinLength')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className={isDark ? 'text-gray-300' : ''}>{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-confirm-password"
                    className={`pr-10 h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className={`w-full h-11 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                disabled={loading}
                data-testid="reset-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('auth.resetting')}
                  </>
                ) : (
                  t('auth.resetButton')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
